/* should only be used on a apolloClient side */

import {
  authLink,
  changeCursorLink,
  httpLink,
  noPermissionErrorToastLink,
  outdatedContractErrorLink,
  toastErrorLink,
  unauthorizedErrorLink,
} from "@/lib/api/apolloLinks.tsx";
import {FGM_PLAN_INFO, FGM_PLAN_LIMIT_FIELDS} from "@/lib/queries/billing.ts";
import {FGM_GLOBAL_ALERT_STATE_FIELDS} from "@/lib/types/fragments.alerting.ts";
import {FGM_BILLING_DATA_FIELDS} from "@/lib/types/fragments.billing.ts";
import {FGM_MONITORING_GROUP_FIELDS} from "@/lib/types/fragments.monitoring.ts";
import {FGM_SERVICE_FIELDS, FGM_SERVICE_USAGE_FIELDS} from "@/lib/types/fragments.services.ts";
import {FGM_DASHBOARD_DEFINITION_FIELDS} from "@/lib/types/fragments.website.ts";
import type {InMemoryCacheConfig} from "@apollo/client";
import {ApolloClient, InMemoryCache, from, gql} from "@apollo/client";
import {createFragmentRegistry} from "@apollo/client/cache";
import {loadDevMessages, loadErrorMessages} from "@apollo/client/dev";
import {removeTypenameFromVariables} from "@apollo/client/link/remove-typename";

if (import.meta.env.DEV) {
  loadDevMessages();
  loadErrorMessages();
}

let link;
if (window.location.hostname.indexOf("dev.uptime") !== -1) {
  link = authLink.concat(httpLink);
} else {
  link = httpLink;
}

const removeTypenameLink = removeTypenameFromVariables();

export const fragmentRegistry = createFragmentRegistry(gql`
  ${FGM_DASHBOARD_DEFINITION_FIELDS}
  ${FGM_GLOBAL_ALERT_STATE_FIELDS}
  ${FGM_MONITORING_GROUP_FIELDS}
  ${FGM_PLAN_LIMIT_FIELDS}
  ${FGM_PLAN_INFO}
  ${FGM_SERVICE_FIELDS}
  ${FGM_SERVICE_USAGE_FIELDS}
  ${FGM_BILLING_DATA_FIELDS}
`);

export const apolloClientInMemoryCacheConfig: InMemoryCacheConfig = {
  typePolicies: {
    // for whatever strange or historical reason, AffectedComponent's is
    // identified (uniquely) not by its id (it is in fact id of the component),
    // but by the .obj_id field. Which is causing all kinds of cache merge issues
    AffectedComponent: {
      keyFields: ["obj_id"],
    },
    // PublicStatusPageAffectedComponent.id is the component id, so two
    // incidents that affect the same component would collide and overwrite
    // each other's status. Disable normalization so each incident keeps its
    // own snapshot.
    PublicStatusPageAffectedComponent: {
      keyFields: false,
    },
    SubscriptionPack: {
      keyFields: ["pack_id"],
    },
    SubscriptionPackTier: {
      keyFields: ["tier_id", "pack_id"],
    },
    CheckDetailsAlert: {
      // backend generates UP state, because we don't have up state alert
      keyFields: ["id", "state_is_up"],
    },
    LocationResponseTimes: {
      fields: {
        data: {
          merge: false,
        },
      },
    },
  },
  fragments: fragmentRegistry,
};

const client = new ApolloClient({
  cache: new InMemoryCache(apolloClientInMemoryCacheConfig),
  link: from([
    unauthorizedErrorLink,
    outdatedContractErrorLink,
    toastErrorLink,
    noPermissionErrorToastLink,
    removeTypenameLink,
    changeCursorLink,
    link,
  ]),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-and-network",
    },
    query: {
      fetchPolicy: "network-only",
    },
  },
});

export default client;
